/**
 * What-If Scenario Modeler
 * "What happens if I add 1 staff member?" / "What if upsell rate hits 50%?"
 * Projected revenue impact with before/after comparisons.
 */
import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { FlaskConical, TrendingUp, DollarSign, Users, ChevronDown, ArrowRight, Zap, RotateCcw } from 'lucide-react';
import { getIndustryTerms } from '@/config/industryTerminology';

// Neutral scenarios for any industry without a bespoke set — no SPLH / DT Wait.
const buildGenericScenarios = (t) => ([
  {
    id: 'pricing', title: 'Increase Pricing 5%', description: 'What if you raised your prices by 5%?',
    variable: 'Price Increase', unit: '%', min: 0, max: 15, step: 1, defaultValue: 0,
    calculate: (val) => ({ revenueChange: val * 980, netImpact: val * 880, notes: val > 0 ? `A ${val}% price rise adds ~$${(val * 980).toLocaleString()}/mo in ${t.revenue.toLowerCase()}. Allowing for minor attrition, net impact is ~$${(val * 880).toLocaleString()}/mo — existing ${t.customersLower} rarely leave over a modest, well-communicated increase.` : 'Adjust the slider to model a price change.' }),
    currentValues: { monthlyRevenue: '$54,200', grossMargin: '62%', avgInvoice: '$1,240' },
  },
  {
    id: 'hire', title: `Hire 1 ${t.employee}`, description: `What if you added one ${t.employee.toLowerCase()} to the team?`,
    variable: 'Additional Hires', unit: 'person', min: 0, max: 3, step: 1, defaultValue: 0,
    calculate: (val) => ({ revenueChange: val * 7200, cost: val * 5600, netImpact: val * 1600, notes: val > 0 ? `Each additional ${t.employee.toLowerCase()} historically adds ~$${(val * 7200).toLocaleString()}/mo in capacity at a cost of ~$${(val * 5600).toLocaleString()}/mo. Net contribution turns positive once fully ramped (typically 6-8 weeks).` : 'Adjust the slider to model team growth.' }),
    currentValues: { headcount: '8', revenuePerHead: '$6,775/mo', utilisation: '82%' },
  },
  {
    id: 'overheads', title: 'Reduce Overheads 10%', description: 'What if you trimmed discretionary overheads by 10%?',
    variable: 'Overhead Reduction', unit: '%', min: 0, max: 25, step: 1, defaultValue: 0,
    calculate: (val) => ({ savings: val * 140, netImpact: val * 140, notes: val > 0 ? `Trimming discretionary overheads by ${val}% saves ~$${(val * 140).toLocaleString()}/mo — software, subscriptions and supplier terms are the usual quick wins, with no impact on delivery.` : 'Adjust the slider to model overhead savings.' }),
    currentValues: { monthlyOverheads: '$14,000', largestCategory: 'Software', reviewedRecently: 'No' },
  },
]);

const QSR_SCENARIOS = [
  {
    id: 'staff',
    title: 'Add 1 Staff Member',
    description: 'What if you added a crew member to the lunch peak shift?',
    variable: 'Extra Staff (lunch peak)',
    unit: 'staff',
    min: 0, max: 3, step: 1, defaultValue: 0,
    calculate: (val) => ({
      splhChange: val === 0 ? 0 : -(val * 2.4),
      revenueChange: val * 1800,
      labourCostChange: val * 2200,
      netImpact: (val * 1800) - (val * 2200),
      throughputChange: val * 14,
      waitTimeChange: -(val * 0.4),
      notes: val > 0 ? `Adding ${val} staff lifts throughput by ${val * 14} cars/hr during peak but increases monthly labour by $${(val * 2200).toLocaleString()}. Net impact depends on whether the revenue captured from shorter wait times exceeds the cost.` : 'Adjust the slider to model scenarios.',
    }),
    currentValues: { splh: '$65.40', labourCost: '25.2%', dtWait: '3.2 min', throughput: '126 cars/hr' },
  },
  {
    id: 'upsell',
    title: 'Improve Upsell Rate',
    description: 'What if your combo attachment rate improved across the network?',
    variable: 'Combo Attachment Rate',
    unit: '%',
    min: 38, max: 65, step: 1, defaultValue: 42,
    calculate: (val) => {
      const baseRate = 42;
      const improvement = val - baseRate;
      return {
        revenueChange: improvement * 320,
        avgTransactionChange: improvement * 0.18,
        netImpact: improvement * 320,
        notes: improvement > 0 ? `Each 1% improvement in combo attachment adds ~$320/month across the network. At ${val}%, projected additional revenue is $${(improvement * 320).toLocaleString()}/month. Southport already hit 52% using the suggestive sell script — replicating this network-wide is realistic.` : 'Adjust the slider above the current 42% network average.',
      };
    },
    currentValues: { attachmentRate: '42%', avgTransaction: '$14.80', monthlyUpsellRev: '$18,400' },
  },
  {
    id: 'hours',
    title: 'Reduce Operating Hours',
    description: 'What if you closed 1 hour earlier on slow nights?',
    variable: 'Close Time Shift',
    unit: 'hr earlier',
    min: 0, max: 2, step: 0.5, defaultValue: 0,
    calculate: (val) => ({
      labourSaved: val * 480 * 5,
      revenueLost: val * 280 * 5,
      netImpact: (val * 480 * 5) - (val * 280 * 5),
      energySaved: val * 120 * 5,
      notes: val > 0 ? `Closing ${val}hr earlier saves $${(val * 480 * 5).toLocaleString()}/month in labour + $${(val * 120 * 5).toLocaleString()} in energy, but loses ~$${(val * 280 * 5).toLocaleString()} in late-evening revenue. Net monthly saving: $${((val * 480 * 5) - (val * 280 * 5) + (val * 120 * 5)).toLocaleString()}.` : 'Adjust the slider to model early close scenarios.',
    }),
    currentValues: { lastHourRev: '$280/hr avg', lastHourLabour: '$480/hr', lastHourTransactions: '18 avg' },
  },
];

const TECH_SCENARIOS = [
  {
    id: 'pricing',
    title: 'Increase Pricing 10%',
    description: 'What if you raised plan prices by 10%?',
    variable: 'Price Increase',
    unit: '%',
    min: 0, max: 20, step: 1, defaultValue: 0,
    calculate: (val) => ({
      mrrChange: val * 1240,
      churnIncrease: val * 0.3,
      netMRR: (val * 1240) - (val * 0.3 * 4200),
      notes: val > 0 ? `A ${val}% price increase adds $${(val * 1240).toLocaleString()} MRR but models show ~${(val * 0.3).toFixed(1)}% churn increase. Net MRR impact: $${((val * 1240) - (val * 0.3 * 4200)).toLocaleString()}/month.` : 'Adjust the slider to model pricing changes.',
    }),
    currentValues: { mrr: '$124,000', churnRate: '3.2%', arpu: '$89' },
  },
  {
    id: 'cs-hire',
    title: 'Hire 1 CS Rep',
    description: 'What if you added a customer success manager?',
    variable: 'Additional CS Reps',
    unit: 'rep',
    min: 0, max: 3, step: 1, defaultValue: 0,
    calculate: (val) => ({
      churnReduction: val * 0.8,
      savedMRR: val * 0.8 * 0.01 * 124000,
      cost: val * 7500,
      netImpact: (val * 0.8 * 0.01 * 124000) - (val * 7500),
      notes: val > 0 ? `Each CS rep historically reduces churn by ~0.8%. At current MRR ($124K), that saves $${(val * 992).toLocaleString()}/month. Cost: $${(val * 7500).toLocaleString()}/month. ROI turns positive in month ${Math.ceil((val * 7500) / (val * 992))}.` : 'Adjust the slider to model CS team expansion.',
    }),
    currentValues: { currentCS: '3 reps', churnRate: '3.2%', avgTicketTime: '4.2 hrs' },
  },
];

export const WhatIfScenarioModeler = ({ industry = 'general' }) => {
  const t = getIndustryTerms(industry);
  const scenarios = t.key === 'hospitality' ? QSR_SCENARIOS : t.key === 'technology' ? TECH_SCENARIOS : buildGenericScenarios(t);
  const [activeScenario, setActiveScenario] = useState(scenarios[0].id);
  const [values, setValues] = useState(() => {
    const init = {};
    scenarios.forEach(s => { init[s.id] = s.defaultValue; });
    return init;
  });

  // Reset active scenario and values when industry changes
  React.useEffect(() => {
    setActiveScenario(scenarios[0].id);
    const init = {};
    scenarios.forEach(s => { init[s.id] = s.defaultValue; });
    setValues(init);
  // eslint-disable-next-line
  }, [industry]);

  const scenario = scenarios.find(s => s.id === activeScenario) || scenarios[0];
  const currentValue = values[activeScenario] ?? scenario.defaultValue;
  const result = scenario.calculate(currentValue);
  const hasChange = currentValue !== scenario.defaultValue;

  return (
    <Card className="glass-card overflow-hidden" data-testid="what-if-scenario-modeler">
      <div className="h-1 bg-gradient-to-r from-[#007AFF] via-blue-500 to-indigo-500" />
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-[#007AFF]" />
            What-If Scenarios
          </CardTitle>
          <Badge variant="outline" className="text-[10px]">
            <Zap className="w-3 h-3 mr-1" />Interactive
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">Model changes and see projected impact before you commit</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Scenario Tabs */}
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {scenarios.map(s => (
            <Button
              key={s.id}
              variant={activeScenario === s.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveScenario(s.id)}
              className={`text-xs whitespace-nowrap shrink-0 ${activeScenario === s.id ? 'bg-[#0066CC] hover:bg-cyan-700' : ''}`}
              data-testid={`scenario-tab-${s.id}`}
            >
              {s.title}
            </Button>
          ))}
        </div>

        {/* Active Scenario */}
        <div className="bg-gradient-to-r from-cyan-50/50 to-blue-50/50 border border-cyan-100 rounded-xl p-4 space-y-4">
          <div>
            <p className="text-sm font-semibold text-slate-800">{scenario.title}</p>
            <p className="text-xs text-slate-500 mt-0.5">{scenario.description}</p>
          </div>

          {/* Current Values */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {Object.entries(scenario.currentValues).map(([key, val]) => (
              <div key={key} className="bg-white/70 rounded-lg p-2 border border-slate-100 text-center">
                <p className="text-[10px] text-slate-400 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                <p className="text-sm font-bold text-slate-800">{val}</p>
              </div>
            ))}
          </div>

          {/* Slider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-slate-700">{scenario.variable}</label>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-cyan-700">
                  {values[activeScenario]}{scenario.unit === '%' ? '%' : ` ${scenario.unit}`}
                </span>
                {hasChange && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0"
                    onClick={() => setValues(v => ({ ...v, [activeScenario]: scenario.defaultValue }))}
                  >
                    <RotateCcw className="w-3 h-3 text-slate-400" />
                  </Button>
                )}
              </div>
            </div>
            <Slider
              value={[values[activeScenario]]}
              onValueChange={([v]) => setValues(prev => ({ ...prev, [activeScenario]: v }))}
              min={scenario.min}
              max={scenario.max}
              step={scenario.step}
              className="w-full"
              data-testid="scenario-slider"
            />
            <div className="flex justify-between text-[10px] text-slate-400">
              <span>{scenario.min}{scenario.unit === '%' ? '%' : ''}</span>
              <span>{scenario.max}{scenario.unit === '%' ? '%' : ` ${scenario.unit}`}</span>
            </div>
          </div>

          {/* Results */}
          {hasChange && (
            <div className="space-y-3">
              {result.netImpact !== undefined && (
                <div className={`rounded-xl p-3 border ${result.netImpact >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                  <p className="text-[10px] uppercase tracking-wider font-semibold mb-1 ${result.netImpact >= 0 ? 'text-emerald-500' : 'text-red-500'}">
                    Projected Net Impact
                  </p>
                  <p className={`text-lg font-bold ${result.netImpact >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {result.netImpact >= 0 ? '+' : ''}{typeof result.netImpact === 'number' ? `$${result.netImpact.toLocaleString()}` : result.netImpact}/mo
                  </p>
                </div>
              )}
              <div className="bg-white/80 rounded-xl p-3 border border-slate-100">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Analysis</p>
                <p className="text-xs text-slate-700 leading-relaxed">{result.notes}</p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default WhatIfScenarioModeler;
